import React, { useState } from "react";
import ToggleField from "../../lib/ui/components/toggle-field/ToggleField";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import AutomaticInterviewEnableDetails, {
    AUTO_INTERVIEW_INVITE_SCOPE,
} from "../ConfirmationModal/AutomaticInterviewEnableDetails";
import { getModalConfig } from "../ConfirmationModal/modalHelper";

/**
 * Automatic Interviews toggle for an existing role, including the
 * enable-confirmation flow (invite scope + eligible ATS stages).
 * Lives in the role page title row.
 */
const AutomaticInterviewsToggle = ({
    automaticInterviews,
    setAutomaticInterviews,
    onSave,
    hasAnalysisRubric = false,
    onAnalysisRubricRequired,
    needsSetup = false,
    onSetupRequired,
    companyId,
    jobId,
    token,
    atsJobId,
    interviewEligibleStageNames,
    disabled = false,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [inviteScope, setInviteScope] = useState(
        AUTO_INTERVIEW_INVITE_SCOPE.NEW_ONLY
    );
    const [isPersisting, setIsPersisting] = useState(false);
    const [selectedEligibleStages, setSelectedEligibleStages] = useState([]);
    const [enableModalReady, setEnableModalReady] = useState({
        loading: false,
        error: "",
        canConfirm: true,
    });

    const applyValue = async (nextValue, inviteOptions = {}) => {
        const previous = automaticInterviews;
        setAutomaticInterviews(nextValue);
        if (typeof onSave !== "function") {
            return;
        }
        setIsPersisting(true);
        try {
            await onSave(nextValue, inviteOptions);
        } catch {
            setAutomaticInterviews(previous);
        } finally {
            setIsPersisting(false);
        }
    };

    const handleToggle = (checked) => {
        if (!checked) {
            void applyValue(false);
            return;
        }
        if (!automaticInterviews) {
            if (needsSetup) {
                onSetupRequired?.();
                return;
            }
            if (!hasAnalysisRubric) {
                onAnalysisRubricRequired?.();
                return;
            }
            setInviteScope(AUTO_INTERVIEW_INVITE_SCOPE.NEW_ONLY);
            setSelectedEligibleStages(
                Array.isArray(interviewEligibleStageNames)
                    ? interviewEligibleStageNames
                    : []
            );
            setEnableModalReady({
                loading: Boolean(atsJobId),
                error: "",
                canConfirm: !atsJobId,
            });
            setConfirmOpen(true);
        }
    };

    const confirmEnable = () => {
        setConfirmOpen(false);
        const includeExisting =
            inviteScope === AUTO_INTERVIEW_INVITE_SCOPE.INCLUDE_EXISTING;
        void applyValue(true, {
            includeExistingWithoutSession: includeExisting,
            interviewEligibleStageNames: selectedEligibleStages,
        });
    };

    return (
        <>
            <ToggleField
                label="Automatic Interviews"
                checked={automaticInterviews}
                disabled={disabled || isPersisting}
                onChange={handleToggle}
                data-cy="automatic-interviews-toggle"
            />
            <ConfirmationModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmEnable}
                {...getModalConfig("enableAutomaticInterviews")}
                details={
                    <AutomaticInterviewEnableDetails
                        value={inviteScope}
                        onChange={setInviteScope}
                        companyId={companyId}
                        jobId={jobId}
                        token={token}
                        atsJobId={atsJobId}
                        initialEligibleStages={interviewEligibleStageNames}
                        selectedStages={selectedEligibleStages}
                        onSelectedStagesChange={setSelectedEligibleStages}
                        onReadyChange={setEnableModalReady}
                    />
                }
                confirmDisabled={!enableModalReady.canConfirm}
                confirmDisabledReason={
                    enableModalReady.loading
                        ? "Loading ATS stages…"
                        : enableModalReady.error ||
                          (atsJobId
                              ? "Turn on at least one stage to enable automatic interviews."
                              : undefined)
                }
                confirmButtonVariant="primary"
                isLoading={isPersisting}
                loadingText="Saving..."
                dismissible={!isPersisting}
            />
        </>
    );
};

export default AutomaticInterviewsToggle;
